import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { get, map, isEmpty, findKey, findLast, forEach } from 'lodash'
import Konva from 'konva'
import { Stage, Layer, Image, Group, Rect } from 'react-konva'
import { useLyricProcessor, useLyricHighlighter } from 'hooks/lyric'
import { List, Row, Col } from 'antd'
import { ArrowRightOutlined, FieldTimeOutlined } from '@ant-design/icons'
import './styles.scss'
import { secondsToMinutes } from 'utils/convert'
import { _EVENTS } from 'utils/EventEmitter'
import { useWindowDimensions } from 'utils/useWindowDimensions'
import { BIG_CANVAS_PROPS } from 'constans/konva'
import { createKonvaVerse } from 'utils/konva'
import { CounterComponents } from 'components/Counter/Counter'
import { useSettings } from 'hooks/settings'
const bg = require('./kr-background-02-darker-02.jpg')

const useAnimatedText = ({ currentVerse, layerRef }) => {
  const konvaRefs = useRef()
  const { lyricsText } = useSettings()
  const lyricsTextIn = useRef(lyricsText)

  const renderKonvaVerse = () => {
    if (!layerRef.current) return
    const layer = layerRef.current

    if (!currentVerse) return
    const currentPosition = currentVerse.index

    const [container] = layer.getChildren(
      (node) => node.getId() === 'base-lyric'
    )
    if (!container) return

    const textProps = lyricsTextIn.current
    konvaRefs.current = createKonvaVerse(container, currentVerse, {
      withText: true,
      textProps,
    })

    const { textBoxes, group } = konvaRefs.current
    group.y(group.y() + 24)

    container.draw()

    new Konva.Tween({
      node: group,
      y: group.y() - 24,
      opacity: 1,
      duration: 0.4,
    }).play()

    return () => {
      const nodes = container.getChildren(
        (node) => node.getId() === `verse_${currentPosition}`
      )
      // node.destroy()
      forEach(nodes, (node) => {
        new Konva.Tween({
          node: node,
          y: node.y() - 24,
          opacity: 0,
          duration: 0.4,
          onFinish: () => {
            node.destroy()
            layer.draw()
          },
        }).play()
      })
    }
  }

  const clearStage = () => {
    if (!konvaRefs.current) return

    const { group } = konvaRefs.current
    group.destroy()
    layerRef.current.draw()
  }

  const reRenderKonvaVerse = () => {
    clearStage()
    renderKonvaVerse()
  }

  useEffect(() => {
    lyricsTextIn.current = lyricsText
    reRenderKonvaVerse()
  }, [get(currentVerse, 'text'), layerRef, lyricsText])

  return true
}

const Background = ({ width, height }) => {
  const [image, setImage] = useState(null)
  useEffect(() => {
    const im = new window.Image()
    im.src = bg
    im.addEventListener('load', () => {
      setImage(im)
    })
  }, [])
  return <Image image={image} width={width} height={height} />
}

const useCanvasAnimation = ({ layerRef }) => {
  useEffect(() => {
    if (!isFinite(currentPosition)) return

    const layer = layerRef.current

    var hexagon = new Konva.RegularPolygon({
      x: layerRef.current.width() / 2,
      y: layerRef.current.height(),
      sides: 6,
      radius: 20,
      fill: 'transparent',
      stroke: 'white',
      strokeWidth: 1,
    })

    layer.add(hexagon)

    var amplitude = 100
    var period = 2000
    // in ms
    var centerX = layerRef.current.width() / 2

    var anim = new Konva.Animation(function (frame) {
      hexagon.x(
        amplitude * Math.sin((frame.time * 2 * Math.PI) / period) + centerX
      )
    }, layer)

    anim.start()
  }, [layerRef.current])
}

export const CanvasSlides = ({
  width,
  height,
  counterProps,
  playerActions,
}) => {

  const { innerWidth, innerHeight } = useWindowDimensions();

  const responsiveWidth = innerWidth * 0.7
  const responsiveHeight = innerHeight * 0.8

  const highlighRef = useRef()
  const layerRef = useRef()

  const { currentVerse } = useLyricProcessor()
  const { setStageRef } = useLyricHighlighter()

  useAnimatedText({ layerRef, currentVerse })

  useEffect(() => {
    setStageRef(layerRef)
  }, [layerRef])

  return (
    <Stage
      width={responsiveWidth}
      height={responsiveHeight}
      style={{ '--window-w': window.outerWidth, '--canvas-w': width }}
    >
      <Layer ref={layerRef}>
        <Background width={width} height={height} />
        <Group id="base-lyric" width={responsiveWidth}
      height={responsiveHeight}></Group>
        <Group
          id="highlited-lyric"
          ref={highlighRef}
          width={responsiveWidth}
      height={responsiveHeight}
        ></Group>
      </Layer>
      <ActionsLayer counterProps={counterProps} playerActions={playerActions} width={responsiveWidth}
      height={responsiveHeight} />
    </Stage>
  )
}

const PlayComponent = ({ height, width, showActions }) => {
  return (
    <Rect
      height={height}
      width={width}
      onTouchStart={showActions}
      onTap={showActions}
      onMouseDown={showActions}
    />
  )
}
const ActionsLayer = ({ counterProps, playerActions, ...props }) => {
  const [playing, setPlaying] = useState(false)
  const { play, pause, stop } = playerActions

  const layer = useRef()

  const handlePlay = useCallback(() => {
    setPlaying(true)
    play()
  }, [])

  const showActions = useCallback(() => {
    if (!layer.current) return
    setPlaying(false)
    pause()

    // const [counterNode] = layer.current.find('#counter')
    // if (counterNode) {
    //   counterNode.show()
    // }
  }, [])

  return (
    <Layer id="actions-layer" ref={layer} {...props}>
      {!isEmpty(counterProps) && !playing && (
        <CounterComponents
          layer={layer}
          {...counterProps}
          {...props}
          onFinish={handlePlay}
        />
      )}
      {playing && <PlayComponent {...props} showActions={showActions} />}
    </Layer>
  )
}
ActionsLayer.defaultProps = {
  height: BIG_CANVAS_PROPS.height,
  width: BIG_CANVAS_PROPS.width,
  playerActions: {
    play: () => {},
    pause: () => {},
    stop: () => {},
  },
}

CanvasSlides.defaultProps = {
  height: BIG_CANVAS_PROPS.height,
  width: BIG_CANVAS_PROPS.width,
  useCounter: false,
  counterProps: {},
}

const ItemTimeline = ({ time }) => {
  if (!isFinite(time)) return <></>
  return (
    <>
      <FieldTimeOutlined /> {secondsToMinutes(time)}
    </>
  )
}
const ListItem = ({ name, isActive, index, onClick, timeline }) => {
  return (
    <List.Item extra={isActive && <ArrowRightOutlined />} onClick={onClick}>
      <List.Item.Meta
        title={name}
        description={<ItemTimeline time={timeline} />}
      />
    </List.Item>
  )
}
ListItem.defaultProps = {
  onItemClick: () => {},
}
const getItemTimelime = (timelineEvents, index) => {
  const time = Number(
    findKey(
      timelineEvents,
      (events) =>
        !!findLast(events, ({ verse }) => get(verse, 'index') == index)
    )
  )
  if (isFinite(time)) return time
}
export const KaraokeSlides = ({ onItemClick, counterProps, playerActions }) => {
  const { items, currentPosition } = useLyricProcessor()
  const listRef = useRef()
  //const { timelineEvents } = useTimelineEvents()

  const dataSource = useMemo(
    () =>
      map(items, (verse) => ({
        ...verse,
        isActive: currentPosition === verse.index,
        onClick: () => onItemClick && onItemClick(verse),
        //timeline: getItemTimelime(timelineEvents, verse.index),
      })),
    [currentPosition, items, onItemClick]
  )

  useEffect(() => {
    if (!listRef.current) return

    const items = listRef.current.querySelectorAll('li')
    if (!items[currentPosition]) return

    const activeItem = items[currentPosition]
    if (!activeItem) return

    const itemHeight = activeItem.offsetHeight
    const itemY = activeItem.offsetTop
    const listHeight = listRef.current.offsetHeight
    const lapView = itemHeight * 3
    if (itemY + itemHeight < listHeight - lapView) {
      if (listRef.current.scrollTop !== 0) listRef.current.scroll(0, 0)

      return
    }

    listRef.current.scroll(0, itemY - listHeight + lapView)
  }, [currentPosition])

  return (
    <Row className="karaoke container">
      <Col className="verses" ref={listRef}>
        <List bordered dataSource={dataSource} renderItem={ListItem} />
      </Col>
      <Col className="canvas-slider">
        {!isEmpty(items) && (
          <CanvasSlides
            counterProps={counterProps}
            playerActions={playerActions}
          />
        )}
      </Col>
    </Row>
  )
}
